import ConversationLifecycle, { ManagedConversationProps } from './ConversationLifecycle';
import {
  useEffect,
  useMemo,
} from 'react';
import {
  useLocation,
} from 'react-router-dom';

import {
  useChatSession,
} from '../hooks/useChatSession';
import {
  useRetziaUser,
} from '../hooks/useRetziaUser';
import {
  navigateWithSession,
  replaceWithSession,
} from '../services/sessionNavigation';
import RetziaActivityWorkspace from './retzia/RetziaActivityWorkspace';
import {
  GRAMMAR_FIRST_MESSAGES,
  GRAMMAR_TOPICS,
  GRAMMAR_TOPIC_INSTRUCTION,
  GRAMMAR_VISUAL_FORMAT_INSTRUCTION,
} from './retzia/workspaceConfig';

const FEEDBACK_THRESHOLD = 3;

function GrammarSession({initialConversation, requestNew, onPersistenceConflict}: ManagedConversationProps) {
  const location = useLocation();

  const language: string =
    location.state?.language ??
    new URLSearchParams(
      location.search
    ).get('language') ??
    '';

  const {
    currentUser,
    checking,
  } = useRetziaUser();

  const baseFirstMessage =
    GRAMMAR_FIRST_MESSAGES[language] ??
    GRAMMAR_FIRST_MESSAGES.english;

  const firstMessage = useMemo(
    () =>
      baseFirstMessage.map((message, index) =>
        index === 0
          ? {
              ...message,
              content: `${message.content} ${GRAMMAR_VISUAL_FORMAT_INSTRUCTION}`,
            }
          : message
      ),
    [baseFirstMessage]
  );

  const session = useChatSession({
    initialConversation,
    onPersistenceConflict,
    language,
    mode: 'grammar',
    firstMessage,
    messagesLimit: Infinity,
    feedbackThreshold:
      FEEDBACK_THRESHOLD,
    neverAutoTTS: true,
    chunkedManualPlay: true,
  });

  useEffect(() => {
    if (!checking && !currentUser) {
      replaceWithSession(
        `/login?language=${encodeURIComponent(
          language
        )}&returnTo=grammar`
      );
    }
  }, [
    checking,
    currentUser,
    language,
  ]);

  if (checking || !currentUser) {
    return null;
  }

  const topicInstruction =
    GRAMMAR_TOPIC_INSTRUCTION[language] ??
    GRAMMAR_TOPIC_INSTRUCTION.english;

  return (
    <RetziaActivityWorkspace
      language={language}
      session={session}
      newConversationLabel="Nuevo chat"
      onNewConversation={() => void requestNew(session.prepareForNewConversation)}
      newConversationDisabled={!session.isConversationReady || session.isWaitingForBot || session.isMicActivated}
      persistenceError={session.persistenceError}
      reportMode="grammar"
      hideFirstMessage
      inputPlaceholder="Escribe aquí…"
      topics={
        GRAMMAR_TOPICS[language] ??
        GRAMMAR_TOPICS.english
      }
      topicPrompt={topic =>
        `${topicInstruction} ${topic}`
      }
      onClose={() =>
        navigateWithSession('/home')
      }
    />
  );
}

function Grammar() {
  const location = useLocation();
  const language: string = location.state?.language ?? new URLSearchParams(location.search).get('language') ?? '';
  return <ConversationLifecycle key={language} language={language} mode="grammar">
    {props => <GrammarSession {...props} />}
  </ConversationLifecycle>;
}
export default Grammar;
